import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
const RedesignModalContext = createContext(null);
// Imperative redesign modal API (same `show`/`hide` ergonomics as the legacy modal context),
// but renders a redesign overlay + card with framer enter/exit animation. The shown node is
// expected to be a `RedesignModalLayout`. Modals stack (replace by default, `replace: false`
// to nest a confirm on top without resizing the base modal).
export const useRedesignModal = () => {
    const ctx = useContext(RedesignModalContext);
    if (!ctx) {
        throw new Error('useRedesignModal must be used within a RedesignModalProvider');
    }
    return ctx;
};
export const RedesignModalProvider = ({ children }) => {
    const [stack, setStack] = useState([]);
    const show = useCallback((node, options = {}) => {
        var _a;
        const entry = { node, closeable: (_a = options.closeable) !== null && _a !== void 0 ? _a : true };
        setStack(prev => (options.replace === false ? [...prev, entry] : [entry]));
    }, []);
    const hide = useCallback(() => setStack(prev => prev.slice(0, -1)), []);
    const top = stack[stack.length - 1];
    const ctx = useMemo(() => { var _a; return ({ show, hide, closeable: (_a = top === null || top === void 0 ? void 0 : top.closeable) !== null && _a !== void 0 ? _a : true }); }, [show, hide, top]);
    useEffect(() => {
        if (stack.length === 0) {
            // A Radix tooltip/popover rendered inside a modal can leave `pointer-events: none` on the
            // body if it unmounts (modal close) while still open, which blocks all clicks on the page.
            // Clear it once every modal is closed so the page stays interactive.
            document.body.style.pointerEvents = '';
        }
    }, [stack.length]);
    useEffect(() => {
        if (!stack.length) {
            return undefined;
        }
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && (top === null || top === void 0 ? void 0 : top.closeable)) {
                hide();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [stack.length, top, hide]);
    return (<RedesignModalContext.Provider value={ctx}>
			{children}
			{typeof document !== 'undefined' && createPortal(<AnimatePresence>
					{stack.map((entry, index) => {
                const isTop = index === stack.length - 1;
                return (
                /* eslint-disable-next-line react/no-array-index-key */
                <Fragment key={index}>
								<motion.div onClick={() => { if (isTop && entry.closeable)
                    hide(); }} className="redesign fixed inset-0 bg-black/70 backdrop-blur-sm" style={{ zIndex: 1100 + index * 2 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}/>
								<motion.div className="pointer-events-none fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1101 + index * 2 }} initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
									<div className="pointer-events-auto flex max-h-full w-full justify-center" onClick={(event) => { if (event.target === event.currentTarget && isTop && entry.closeable)
                    hide(); }}>
										{entry.node}
									</div>
								</motion.div>
							</Fragment>);
            })}
				</AnimatePresence>, document.body)}
		</RedesignModalContext.Provider>);
};
